import React, { useCallback, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import cn from 'classnames';
import moment from 'moment';
import Tooltip from '../General/Tooltip';
import Loader from '../General/Loader';
import axios from 'axios';
import { getProductMentionQueue, getProductMentionStats, setProductMentionReview, setProductMentionExemplar, setProductMentionQualityScore, rejectProductMentionRuns, retryProductMentionRuns, scoreProductMention } from '../../api/product_mentions';
import { getPrettyNumber } from '../../helpers/formatting';
import { REVIEW_TABS, UNSCORED_TAB, getCategoryFilterParams } from '../../helpers/product_mention_helpers';
import ProductMentionsControls from './ProductMentionsControls';
import ProductMentionsGrid from './ProductMentionsGrid';
import ProductMentionsTable from './ProductMentionsTable';
import ProductMentionDrawer from './ProductMentionDrawer';
import './ProductMentions.scss';

const ProductMentions = () => {
  const [rows, setRows] = useState([]);
  const [tabCounts, setTabCounts] = useState([]);
  const [methodCounts, setMethodCounts] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState('pending');
  const [filters, setFilters] = useState({ methods: [], bands: [], platforms: [], tiers: [], q: '', brand: null, category: '', scores: [] });
  const [view, setView] = useState('grid');
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [checked, setChecked] = useState([]);
  const [drawer, setDrawer] = useState(null);
  const [scoreResult, setScoreResult] = useState(null);
  const [isScoring, setIsScoring] = useState(false);
  const scoreCancelRef = useRef(null);
  const [beforeId, setBeforeId] = useState(null);
  const [beforeStack, setBeforeStack] = useState([]);
  const unscoredReturnTab = useRef('pending');
  const tabConfig = tab === UNSCORED_TAB.key ? UNSCORED_TAB : REVIEW_TABS.find(entry => entry.key === tab);
  const isRunTab = tabConfig.kind === 'run';
  const canFilterByProduct = tab !== 'unmatched';
  const hasProductFilters = canFilterByProduct && Boolean(filters.brand || filters.category);
  // Mirrors which filters fetchRows actually applies on this tab, so ignored
  // chips can't suppress the tab's empty copy.
  const hasListFilters = Boolean(
    filters.platforms.length || filters.tiers.length || (!isRunTab && (filters.methods.length || filters.bands.length || filters.scores.length))
  );

  // Data fetching — the fetch id drops slow responses from a superseded filter
  // or page. Background fetches re-sync rows and counts without the loader.
  const fetchIdRef = useRef(0);
  const fetchRows = useCallback(({ background, withCounts } = {}) => {
    const fetchId = ++fetchIdRef.current;
    if (!background) setIsLoading(true);
    const params = {};
    if (tab) params.reviewStatus = tab;
    if (!isRunTab && filters.methods.length) params.method = filters.methods.join(',');
    if (!isRunTab && filters.bands.length) params.band = filters.bands.join(',');
    if (!isRunTab && filters.scores.length) params.adminQualityScore = filters.scores.join(',');
    if (filters.platforms.length) params.platform = filters.platforms.join(',');
    if (filters.tiers.length) params.tier = filters.tiers.join(',');
    if (filters.q) params.q = filters.q;
    if (canFilterByProduct && filters.brand) params.AllBrand_id = filters.brand.id;
    if (canFilterByProduct && filters.category) Object.assign(params, getCategoryFilterParams(filters.category));
    if (beforeId) params.beforeId = beforeId;
    if (!withCounts && beforeId) params.counts = 0;
    getProductMentionQueue(params)
      .then(data => {
        if (fetchId !== fetchIdRef.current) return;
        setRows(data.productMentions);
        setHasMore(data.hasMore);
        if (data.tabCounts) {
          setTabCounts(data.tabCounts);
          setMethodCounts(data.methodCounts);
        }
      })
      .catch(err => window.ALERT.error(err))
      .finally(() => fetchId === fetchIdRef.current && setIsLoading(false));
  }, [tab, filters, beforeId, isRunTab, canFilterByProduct]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const fetchStats = () =>
    getProductMentionStats()
      .then(setStats)
      .catch(err => window.ALERT.error(err));
  useEffect(() => {
    fetchStats();
  }, []);

  // Tabs, filters, paging
  const resetPaging = () => {
    stopScoring();
    setChecked([]);
    setBeforeId(null);
    setBeforeStack([]);
  };
  const changeTab = key => {
    resetPaging();
    setTab(key);
  };
  const changeFilters = updates => {
    resetPaging();
    if (tab === UNSCORED_TAB.key && updates.scores?.length) setTab('');
    setFilters(prev => ({ ...prev, ...updates }));
  };
  const toggleUnscored = () => {
    resetPaging();
    if (tab === UNSCORED_TAB.key) {
      setTab(unscoredReturnTab.current);
      return;
    }
    unscoredReturnTab.current = tab;
    setFilters(prev => ({ ...prev, scores: [] }));
    setTab(UNSCORED_TAB.key);
  };
  const goToPrevPage = () => {
    stopScoring();
    setChecked([]);
    setBeforeId(beforeStack[beforeStack.length - 1] ?? null);
    setBeforeStack(prev => prev.slice(0, -1));
  };
  const goToNextPage = () => {
    stopScoring();
    const lastId = rows[rows.length - 1]?.id;
    if (!lastId) return;
    setChecked([]);
    setBeforeStack(prev => [...prev, beforeId]);
    setBeforeId(lastId);
  };

  // Review actions — checked ids or a single row. Optimistic: rows flip
  // locally right away, a background refetch reconciles counts and page fill,
  // and a failed request restores the server's truth.
  const toggleChecked = id => setChecked(prev => (prev.includes(id) ? prev.filter(other => other !== id) : [...prev, id]));
  const saveThenRefresh = save =>
    save
      .then(() => {
        fetchRows({ background: true, withCounts: true });
        fetchStats();
      })
      .catch(err => {
        window.ALERT.error(err);
        fetchRows({ background: true, withCounts: true });
      });
  const submitReview = (ids, reviewStatus) => {
    stopScoring();
    setChecked(prev => prev.filter(id => !ids.includes(id)));
    setRows(prev =>
      (tab === UNSCORED_TAB.key ? reviewStatus === 'rejected' : Boolean(tab))
        ? prev.filter(row => !ids.includes(row.id))
        : prev.map(row =>
            ids.includes(row.id)
              ? {
                  ...row,
                  reviewStatus,
                  status: reviewStatus === 'rejected' ? 'invalidated' : 'active',
                  OverrideProduct_id: null,
                  isExemplar: reviewStatus === 'rejected' ? false : row.isExemplar,
                  adminQualityScore: reviewStatus === 'rejected' || row.OverrideProduct_id ? null : row.adminQualityScore,
                  qualityScore: reviewStatus === 'rejected' || row.OverrideProduct_id ? null : row.qualityScore
                }
              : row
          )
    );
    saveThenRefresh(setProductMentionReview(ids, reviewStatus));
  };

  const submitQualityScore = async (row, adminQualityScore) => {
    const saved = await setProductMentionQualityScore(row.id, adminQualityScore, row.OverrideProduct_id || row.Product_id);
    fetchRows({ background: true });
    return saved;
  };

  const submitExemplar = row => {
    const isExemplar = !row.isExemplar;
    setRows(prev => prev.map(other => (other.id === row.id ? { ...other, isExemplar } : other)));
    saveThenRefresh(setProductMentionExemplar(row.id, isExemplar));
  };

  const submitRejectRuns = ids => {
    setChecked(prev => prev.filter(id => !ids.includes(id)));
    setRows(prev => prev.filter(row => !ids.includes(row.id)));
    saveThenRefresh(rejectProductMentionRuns(ids));
  };

  const submitRetry = ids => {
    setChecked(prev => prev.filter(id => !ids.includes(id)));
    saveThenRefresh(retryProductMentionRuns(ids));
  };

  useEffect(() => () => {
    scoreCancelRef.current?.cancel();
    scoreCancelRef.current = null;
  }, []);

  const rememberScore = result => {
    setScoreResult(result);
    setRows(prev =>
      tab === UNSCORED_TAB.key
        ? prev.filter(other => other.id !== result.id)
        : prev.map(other => (other.id === result.id ? { ...other, qualityScore: result.qualityScore } : other))
    );
    fetchRows({ background: true, withCounts: true });
    return result;
  };
  const stopScoring = () => {
    scoreCancelRef.current?.cancel();
    scoreCancelRef.current = null;
    setIsScoring(false);
  };
  const runScore = id => {
    scoreCancelRef.current?.cancel();
    const source = axios.CancelToken.source();
    scoreCancelRef.current = source;
    setIsScoring(true);
    return scoreProductMention(id, { cancelToken: source.token })
      .then(result => scoreCancelRef.current === source ? rememberScore(result) : null)
      .catch(err => {
        if (!axios.isCancel(err)) window.ALERT.error(err);
      })
      .finally(() => {
        if (scoreCancelRef.current === source) {
          scoreCancelRef.current = null;
          setIsScoring(false);
        }
      });
  };

  const tabCountFor = key => tabCounts.find(entry => entry.reviewStatus === key)?.count || 0;

  // Pipeline health strip
  const runCount = status => stats?.runs.find(run => run.status === status)?.count || 0;
  const failedRuns = runCount('failed');
  const healthStats = stats
    ? [
        { label: 'completed', value: runCount('completed') },
        { label: 'failed', value: failedRuns, tone: failedRuns ? 'warn' : null },
        { label: 'queue', value: stats.queueDepth ? stats.queueDepth.visible + stats.queueDepth.inFlight + stats.queueDepth.delayed : '—' }
      ]
    : [];
  const trendMetrics = stats ? getTrendMetrics(stats.daily) : [];

  const rowHandlers = {
    onOpen: row => setDrawer({ id: row.id, kind: row.kind || 'mention' }),
    onCheck: row => toggleChecked(row.id),
    onConfirm: row => submitReview([row.id], 'confirmed'),
    onRetry: row => submitRetry([row.id]),
    isChecked: row => checked.includes(row.id),
    canSelect: tabConfig.canSelect !== false,
    showStatus: Boolean(tabConfig.showStatus)
  };

  // The bulk bar only renders with selections, and only selectable tabs can
  // have them — so on run tabs this always means the unmatched queue.
  const bulkActions = isRunTab
    ? [
        { display: 'Try again', className: 'retry', onClick: () => submitRetry(checked) },
        { display: 'Correct — no product', className: 'reject', onClick: () => submitRejectRuns(checked) }
      ]
    : [
        { display: 'Confirm', className: 'confirm', onClick: () => submitReview(checked, 'confirmed') },
        { display: 'Not a product', className: 'reject', onClick: () => submitReview(checked, 'rejected') }
      ];

  return (
    <div className='product-mentions'>
      <div className='pm-header'>
        <div className='pm-header-copy'>
          <h1 className='pm-title'>Product Mentions</h1>
          <div className='pm-subtitle'>Confirm hits, tag misses, override anything.</div>
        </div>
        <div className='pm-health'>
          {healthStats.map(({ label, value, tone }) => (
            <div key={label} className={cn('pm-stat', tone)}>
              <div className='pm-stat-value'>{value}</div>
              <div className='pm-stat-label'>{label}</div>
            </div>
          ))}
          {trendMetrics.map(({ key, days, max }) => (
            <div key={key} className='pm-trend'>
              <div className='pm-trend-bars'>
                {days.map(day => (
                  <Tooltip key={day.day} message={`${moment(day.day).format('MMM D')} — ${day.count} ${key}`} outerClassName='pm-trend-tip'>
                    <div className={cn('pm-trend-bar', { empty: !day.count })} style={{ height: `${barHeight(day.count, max)}px` }} />
                  </Tooltip>
                ))}
              </div>
              <div className='pm-trend-label'>{key}</div>
            </div>
          ))}
        </div>
      </div>

      <div className='pm-tabs'>
        {REVIEW_TABS.map(({ key, display, isWorkQueue }) => {
          const count = tabCountFor(key);
          const countDisplay = getPrettyNumber(count);
          const isShortened = countDisplay !== String(count);
          const countEl = isWorkQueue && count > 0 ? <span className='pm-tab-badge'>{countDisplay}</span> : <span className='pm-tab-count'>{countDisplay}</span>;
          return (
            <div key={key} className={cn('pm-tab', { active: tab === key })} onClick={() => changeTab(key)}>
              {display}
              {isShortened ? <Tooltip message={count.toLocaleString()} outerClassName='pm-tab-tip'>{countEl}</Tooltip> : countEl}
            </div>
          );
        })}
      </div>

      <ProductMentionsControls
        filters={filters}
        onChange={changeFilters}
        methodCounts={methodCounts}
        view={view}
        onView={setView}
        hideSignalFilters={isRunTab}
        hideProductFilters={!canFilterByProduct}
        isUnscored={tab === UNSCORED_TAB.key}
        unscoredCount={tabCountFor(UNSCORED_TAB.key)}
        onToggleUnscored={toggleUnscored}
      />

      {checked.length > 0 && (
        <div className='pm-bulk-bar'>
          <span className='pm-bulk-label'>{checked.length} selected</span>
          <div className='pm-bulk-actions'>
            {bulkActions.map(({ display, className, onClick }) => (
              <button type='button' key={display} className={className} onClick={onClick}>
                {display}
              </button>
            ))}
            <button type='button' className='clear' onClick={() => setChecked([])}>
              Clear
            </button>
          </div>
        </div>
      )}

      <main className='pm-results'>
        {isLoading ? (
          <div className='pm-placeholder'>
            <Loader size={48} />
          </div>
        ) : !rows.length ? (
          <div className='pm-placeholder'>{(!filters.q && !hasProductFilters && !hasListFilters && tabConfig.emptyCopy) || 'No mentions match these filters.'}</div>
        ) : view === 'grid' ? (
          <ProductMentionsGrid rows={rows} {...rowHandlers} />
        ) : (
          <ProductMentionsTable rows={rows} {...rowHandlers} />
        )}
        {(beforeStack.length > 0 || hasMore) && !isLoading && (
          <div className='pm-pagination'>
            <div className='pagination-info'>Page {beforeStack.length + 1}</div>
            <div className='pagination-buttons'>
              <button type='button' disabled={beforeStack.length === 0} onClick={goToPrevPage}>
                Previous
              </button>
              <button type='button' disabled={!hasMore} onClick={goToNextPage}>
                Next
              </button>
            </div>
          </div>
        )}
      </main>

      {drawer && (
        <ProductMentionDrawer
          id={drawer.id}
          kind={drawer.kind}
          onExemplar={submitExemplar}
          onQualityScore={submitQualityScore}
          scoreResult={scoreResult?.id === drawer.id ? scoreResult : null}
          isScoring={isScoring}
          onScoreMention={() => runScore(drawer.id)}
          onClose={() => setDrawer(null)}
          onChanged={() => {
            stopScoring();
            setScoreResult(null);
            setDrawer(null);
            fetchRows({ background: true, withCounts: true });
            fetchStats();
          }}
        />
      )}
    </div>
  );
};

// Zero-filled 14-day series for each trend strip; missing days must render as
// gaps, not vanish. Day keys are UTC to match the server's DATE() grouping.
const getTrendMetrics = daily => {
  const byDay = _.keyBy(daily, 'day');
  return ['runs', 'rows'].map(key => {
    const days = _.range(13, -1, -1).map(offset => {
      const day = moment.utc().subtract(offset, 'days').format('YYYY-MM-DD');
      return { day, count: Number(byDay[day]?.[key] || 0) };
    });
    return { key, days, max: Math.max(1, ...days.map(entry => entry.count)) };
  });
};

const TREND_BAR_MAX_PX = 20;
const barHeight = (count, max) => (count ? _.clamp(Math.round((count / max) * TREND_BAR_MAX_PX), 3, TREND_BAR_MAX_PX) : 2);

export default ProductMentions;
